import { initialState } from './initialState';

// Current state version - increment this when you make breaking changes to state structure
export const CURRENT_STATE_VERSION = 1;

const migrations: Record<number, (state: any) => any> = {
  0: (state: any) => {
    // Migration from v0 to v1: Preserve essential user data while ensuring proper structure
    const migratedState = { ...initialState };

    if (state && typeof state === 'object') {
      // Preserve authentication & user profile
      if (state.auth && typeof state.auth === 'object') {
        migratedState.auth = { ...migratedState.auth, ...state.auth };
      }

      // Preserve social users data
      if (state.users && typeof state.users === 'object') {
        migratedState.users = { ...migratedState.users, ...state.users };
      }
    }

    return migratedState;
  },
  // Add more migrations as needed when you increment CURRENT_STATE_VERSION
};

// Main migration function
export const migrateState = (state: any, fromVersion?: number) => {
  const currentVersion = fromVersion ?? state._persist?.version ?? 0;

  // If state is already current version, return as-is
  if (currentVersion >= CURRENT_STATE_VERSION) {
    return state;
  }

  let migratedState = { ...state };

  // Apply migrations sequentially with error handling
  for (
    let version = currentVersion;
    version < CURRENT_STATE_VERSION;
    version++
  ) {
    if (migrations[version]) {
      try {
        migratedState = migrations[version](migratedState);
      } catch (error) {
        console.error(`Migration ${version} failed:`, error);
        throw new Error(`Migration from version ${version} failed: ${error}`);
      }
    }
  }

  return migratedState;
};

// Fallback function to handle corrupted or incompatible state
export const createFreshState = async (): Promise<void> => {
  try {
    // Clear the persisted state
    localStorage.removeItem('persist:root');

    // Optionally clear other related storage items if needed
    // await AsyncStorage.removeItem('someOtherKey');
  } catch (error) {
    console.error('Failed to clear persisted state:', error);
    // Handle error but don't throw - we want the app to continue with fresh state
  }
};

// State validator to ensure state structure is valid
export const isValidState = (state: any): boolean => {
  try {
    // Check if state exists and is an object
    if (!state || typeof state !== 'object') {
      return false;
    }

    const requiredKeys = ['auth', 'users', 'shows', 'participants'];

    return requiredKeys.every((key) => key in state);
  } catch (error) {
    console.error('Failed to validate state:', error);
    return false;
  }
};

// Transform function for redux-persist (simplified since we don't need transforms)
export const stateTransform = {
  in: (inboundState: any, _key: string | number | symbol) => {
    return inboundState;
  },

  out: (outboundState: any, _key: string | number | symbol) => {
    return outboundState;
  },
};
